/**
 * Two linkages overlap when they wake on the same capability and run the
 * same capability (Feature 74 D5, Feature 77 D28), whether each wakes on
 * its call or its completion (Feature 78 D9): two linkages both starting on
 * BANT and both running product suggestion make two suggestions for every
 * analysis. Different targets on one source are normal and stay
 * quiet, and a linkage that wakes on no completion never overlaps: a schedule
 * wakes on none, its first task starting only its own run (Feature 80 D21). The
 * platform asks this of its templates, where an overlap is possible; an
 * organization of what is on there, where it is actual (Feature 74 D11).
 */
import type { LinkageTemplate } from '@optum/api-client/types';

import { isCall, isSchedule } from '../linkages/start.js';

/** A linkage, as an overlap sees it. */
export interface Automation {
  /** The linkage template's key. */
  id: string;
  name: string;
  /** The capability whose completion, or whose call, wakes it; null when none does. */
  source: string | null;
  /** Whether it wakes on its capability's call rather than its completion (Feature 78). */
  called?: boolean | undefined;
  /** The capabilities its steps run. */
  targets: readonly string[];
}

/** A linkage template: it wakes on its first capability and runs every step's. */
export function fromLinkageTemplate(t: LinkageTemplate): Automation {
  return {
    id: t.key,
    name: t.name,
    source: isSchedule(t.start) ? null : t.first_capability_id,
    called: isCall(t.start),
    targets: [...new Set(t.graph.steps.map((s) => s.capability_id))],
  };
}

/** When an automation wakes on its source: `is called` or `completes`. */
export function wakeWords(a: Automation): string {
  return a.called ? 'is called' : 'completes';
}

/** Each linkage's overlaps, by its key; one with none is absent. */
export function overlapsOf(automations: readonly Automation[]): Map<string, Automation[]> {
  const found = new Map<string, Automation[]>();
  for (const a of automations) {
    if (a.source === null) continue;
    const others = automations.filter(
      (b) =>
        b !== a && b.source === a.source && b.targets.some((target) => a.targets.includes(target)),
    );
    if (others.length > 0) found.set(a.id, others);
  }
  return found;
}

/** "the linkage template BANT-Product-Matcher", for a flag or a panel. */
export function describe(a: Automation, templates: boolean): string {
  return `the linkage${templates ? ' template' : ''} ${a.name}`;
}
